/**
 * ReviewStatusPill — Phase 07
 *
 * Status indicator pill for engine review states.
 * Shows icon + color + text for each state.
 * Accessible via aria-live for screen readers.
 */

import React from 'react';
import type { EngineReviewState } from '../../../types/engineReview';
import { REVIEW_STATE_DISPLAY } from '../../../types/engineReview';

// =============================================================================
// ICONS
// =============================================================================

const ClockIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const EyeIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const XIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const RefreshIcon = ({ spinning = false }: { spinning?: boolean }) => (
  <svg
    className={`w-4 h-4 ${spinning ? 'animate-spin' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const AlertIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

const SkipIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 5l7 7-7 7M5 5l7 7-7 7"
    />
  </svg>
);

// =============================================================================
// COLOR CLASSES
// =============================================================================

// Hue law (K3 review 2026-08-23): a status chip is a restrained marker, never a
// tinted wash, and hue carries MEANING — not decoration. Neutral/in-review/
// pending → hueless (grey); verified/approved → hueless VERIFIED (never green);
// adverse/disputed/failed → crimson; warnings → the needs-human marker. All
// transparent-bg + a 1px inset ring (the hue lives on text + ring, not a fill).
const colorStyles: Record<string, React.CSSProperties> = {
  // gray + blue both read as quiet neutral (in-review is not wayfinding).
  gray: {
    background: 'transparent',
    color: 'var(--color-text-secondary)',
    boxShadow: 'inset 0 0 0 1px var(--color-border)',
  },
  blue: {
    background: 'transparent',
    color: 'var(--color-text-secondary)',
    boxShadow: 'inset 0 0 0 1px var(--color-border)',
  },
  // approved/verified reads quiet — hueless verified grey, never green.
  green: {
    background: 'transparent',
    color: 'var(--color-verified)',
    boxShadow: 'inset 0 0 0 1px var(--color-border)',
  },
  // adverse/disputed/failed — crimson's exclusive channel.
  red: {
    background: 'transparent',
    color: 'var(--color-blocker)',
    boxShadow: 'inset 0 0 0 1px var(--color-blocker)',
  },
  // warnings — the needs-human marker (orange as a restrained signal).
  yellow: {
    background: 'transparent',
    color: 'var(--color-needs-human)',
    boxShadow: 'inset 0 0 0 1px var(--color-border)',
  },
};

// =============================================================================
// COMPONENT
// =============================================================================

export interface ReviewStatusPillProps {
  status: EngineReviewState;
  /** Optional: show reviewer name (e.g., "Approved by Jane Doe") */
  reviewerName?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional class names */
  className?: string;
  /** Test ID for testing */
  testId?: string;
}

function getIcon(iconName: string, isPending: boolean = false): React.ReactNode {
  switch (iconName) {
    case 'clock':
      return <ClockIcon />;
    case 'eye':
      return <EyeIcon />;
    case 'check':
      return <CheckIcon />;
    case 'x':
      return <XIcon />;
    case 'refresh':
      return <RefreshIcon spinning={isPending} />;
    case 'alert':
      return <AlertIcon />;
    case 'skip':
      return <SkipIcon />;
    default:
      return <ClockIcon />;
  }
}

export default function ReviewStatusPill({
  status,
  reviewerName,
  size = 'md',
  className = '',
  testId,
}: ReviewStatusPillProps) {
  // A degraded/absent review payload can hand us a status that isn't a known
  // EngineReviewState (or is undefined). Without a fallback, REVIEW_STATE_DISPLAY[status]
  // is undefined and `display.color` throws — crashing the whole engine-review-detail /
  // attestation page to the error boundary. Degrade to the honest 'unreviewed' default
  // (never falsely "approved").
  const display = REVIEW_STATE_DISPLAY[status] ?? REVIEW_STATE_DISPLAY.unreviewed;
  const colors = colorStyles[display.color] ?? colorStyles.gray;
  const isPending = status === 'rerun_pending';

  // Build the label text
  let label = display.label;
  if (reviewerName && (status === 'approved' || status === 'waived')) {
    label = `${display.label} by ${reviewerName}`;
  }

  // Size-based classes
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-3 py-1 text-sm gap-1.5',
    lg: 'px-4 py-1.5 text-base gap-2',
  };

  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={`Engine status: ${label}. ${display.description}`}
      data-testid={testId || `status-pill-${status}`}
      style={colors}
      className={`
        inline-flex items-center font-medium rounded-[2px]
        ${sizeClasses[size]}
        ${isPending ? 'animate-pulse' : ''}
        ${className}
      `.trim()}
    >
      {getIcon(display.icon, isPending)}
      <span>{label}</span>
    </span>
  );
}

// =============================================================================
// EXPORTS
// =============================================================================

export { ReviewStatusPill };
